import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from './api';
import { LangSwitch, tr } from './i18n';
import { useSession, useTenant } from './session';
import { TourProvider, useTour } from './tour';
import { AssistantProvider } from './assistant/panel';
import { BrandMark, Button, Icons, ThemeSwitch, ago, stateLabel } from './ui';

function Brand({ sub }: { sub: string }) {
  return (
    <div className="brand">
      <BrandMark className="brand-mark" />
      <div>
        <div className="brand-name">ZeroGap</div>
        <div className="brand-sub">{sub}</div>
      </div>
    </div>
  );
}

function SideFoot() {
  const { me, refresh } = useSession();
  const nav = useNavigate();
  const qc = useQueryClient();
  const tour = useTour();
  return (
    <div className="side-foot">
      <div className="mono" style={{ color: 'var(--side-ink)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{me?.user.email}</div>
      <div className="row" style={{ marginTop: 6, gap: 14 }}>
        <NavLink to="/account" style={{ color: 'var(--side-muted)' }}>{tr('Account')}</NavLink>
        <NavLink to="/accessibility" style={{ color: 'var(--side-muted)' }}>{tr('Accessibility')}</NavLink>
        {me?.isSuperAdmin && <NavLink to="/admin" style={{ color: 'var(--side-muted)' }}>{tr('Operator console')}</NavLink>}
        <button
          onClick={async () => {
            await post('/api/auth/logout');
            qc.clear();
            await refresh();
            nav('/login');
          }}
        >
          {tr('Sign out')}
        </button>
        {tour.available && <button type="button" data-tour="tour-replay" onClick={tour.start}>{tr('Take the tour')}</button>}
        <LangSwitch />
        <ThemeSwitch />
      </div>
    </div>
  );
}

function SupportBanner() {
  const { me, refresh } = useSession();
  const { tenantId } = useParams();
  const s = me?.supportSessions.find((x) => x.tenantId === tenantId) ?? me?.supportSessions[0];
  if (!s) return null;
  return (
    <div className="support-banner" role="status">
      <strong>{tr('Operator support session')}</strong>
      <span>
        {s.tenantName} · {s.scope === 'content' ? tr('content access enabled') : tr('no file-content access')} · {tr('reason: “{reason}”', { reason: s.reason })} · {tr('ends {time}', { time: ago(s.expiresAt) })}
      </span>
      <Button size="sm" onClick={async () => { await del(`/api/admin/support-sessions/${s.id}`); await refresh(); }}>{tr('End session')}</Button>
    </div>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { me } = useSession();
  const t = useTenant();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);
  const alerts = useQuery({
    queryKey: ['alerts-count', t.tenantId],
    queryFn: () => get(`/api/t/${t.tenantId}/alerts?state=open`),
    enabled: !!t.tenantId,
    refetchInterval: 60_000,
  });
  const tenants = [...(me?.memberships ?? []).map((m) => ({ id: m.tenantId, name: m.tenantName })), ...(me?.supportSessions ?? []).map((s) => ({ id: s.tenantId, name: tr('{name} (support)', { name: s.tenantName }) }))];
  const openAlerts = alerts.data?.alerts?.length ?? 0;
  return (
    <TourProvider onMenuChange={setMenuOpen}>
    <AssistantProvider>
    <div className="shell">
      <aside className={`side ${menuOpen ? 'open' : ''}`}>
        <div className="side-top">
          <Brand sub={tr('Backup')} />
          <button type="button" className="menu-btn" aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? tr('Close') : tr('Menu')}</button>
        </div>
        {tenants.length > 0 && (
          <div className="tenant-switch">
            <label className="sr-only" htmlFor="tenant">{tr('Organization')}</label>
            <select id="tenant" value={t.tenantId ?? ''} onChange={(e) => nav(`/t/${e.target.value}`)}>
              {!t.tenantId && <option value="">{tr('Choose organization…')}</option>}
              {tenants.map((x) => (
                <option key={x.id} value={x.id}>{x.name}</option>
              ))}
            </select>
          </div>
        )}
        {t.tenantId && (
          <nav className="nav" aria-label={tr('Main')}>
            <NavLink end to={t.base}><Icons.dash />{tr('Overview')}</NavLink>
            <NavLink to={`${t.base}/devices`} data-tour="nav-devices"><Icons.devices />{tr('Computers and phones')}</NavLink>
            <NavLink to={`${t.base}/plans`} data-tour="nav-plans"><Icons.policy />{tr('Backup plans')}</NavLink>
            <NavLink to={`${t.base}/restore`} data-tour="nav-restores"><Icons.restore />{tr('Get files back')}</NavLink>
            {t.can('shares.create') && <NavLink to={`${t.base}/shares`}><Icons.share />{tr('Shared links')}</NavLink>}
            <NavLink to={`${t.base}/alerts`} data-tour="nav-alerts"><Icons.alert />{tr('Alerts')}{openAlerts > 0 && <span className="count"><span aria-hidden="true">{openAlerts}</span><span className="sr-only">{tr('{n} open', { n: openAlerts })}</span></span>}</NavLink>
            <div className="nav-label">{tr('Organization')}</div>
            <NavLink to={`${t.base}/users`}><Icons.users />{tr('Team')}</NavLink>
            <NavLink to={`${t.base}/audit`}><Icons.audit />{tr('Activity')}</NavLink>
            <NavLink to={`${t.base}/billing`}><Icons.bill />{tr('Subscription & usage')}</NavLink>
            <NavLink to={`${t.base}/settings`}><Icons.gear />{tr('Settings')}</NavLink>
          </nav>
        )}
        <SideFoot />
      </aside>
      <div className="main">
        <SupportBanner />
        {t.status !== 'active' && t.tenantId && (
          <div className="env-banner">
            {tr('This organization is {status}. Existing backups remain browsable and restorable; new backups and enrollments may be paused.', { status: stateLabel(t.status).toLowerCase() })}
          </div>
        )}
        {me && !me.user.emailVerified && (
          <div className="env-banner">
            {tr('Verify your email address to add computers.')} <a href="#" onClick={async (e) => { e.preventDefault(); await post('/api/auth/verify-email/resend'); }}>{tr('Resend the email')}</a>
          </div>
        )}
        <main className="content" id="main">{children ?? <Outlet />}</main>
      </div>
    </div>
    </AssistantProvider>
    </TourProvider>
  );
}

export function AdminShell() {
  const { me } = useSession();
  if (!me?.isSuperAdmin) return <div className="content"><h1>{tr('Operators only')}</h1><p>{tr('This console is for platform operators.')}</p></div>;
  return (
    <div className="shell">
      <aside className="side">
        <Brand sub={tr('Operator console')} />
        <nav className="nav" aria-label={tr('Operator')}>
          <NavLink end to="/admin"><Icons.dash />{tr('Overview')}</NavLink>
          <NavLink to="/admin/tenants"><Icons.users />{tr('Tenants')}</NavLink>
          <NavLink to="/admin/plans"><Icons.bill />{tr('Plans & limits')}</NavLink>
          <NavLink to="/admin/users"><Icons.user />{tr('Account recovery')}</NavLink>
          <NavLink to="/admin/queues"><Icons.queue />{tr('Jobs & queues')}</NavLink>
          <NavLink to="/admin/storage"><Icons.server />{tr('Storage health')}</NavLink>
          <NavLink to="/admin/maintenance"><Icons.wrench />{tr('Maintenance')}</NavLink>
          <NavLink to="/admin/releases"><Icons.rocket />{tr('Agent releases')}</NavLink>
          <NavLink to="/admin/audit"><Icons.audit />{tr('Audit')}</NavLink>
        </nav>
        <SideFoot />
      </aside>
      <div className="main">
        <SupportBanner />
        {!me.user.mfaEnabled && <div className="env-banner">{tr('Operators must enable two-factor authentication. Go to Account → Security.')}</div>}
        <main className="content"><Outlet /></main>
      </div>
    </div>
  );
}
